import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Aurora } from "../effects/Aurora";
import { OrbitRings } from "../effects/OrbitRings";
import { MagneticButton } from "../effects/MagneticButton";
import { InfrastructureMesh } from "./InfrastructureMesh";




export const Hero = () => {
    const ref = useRef(null);
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yMesh = useTransform(scrollYProgress, [0, 1], [0, -40]);
    const spotlightY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const spotlightOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

    return (
        <section
            ref={ref}
            className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Ambient aurora */}
            <Aurora />

            {/* Headline spotlight cone */}
            <motion.div
                style={{ y: spotlightY, opacity: spotlightOpacity }}
                aria-hidden
                className="absolute inset-x-0 top-0 h-[80vh] pointer-events-none"
            >
                <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[120%] h-full bg-[radial-gradient(ellipse_55%_60%_at_50%_15%,rgba(59,130,246,0.28),rgba(94,234,212,0.10)_35%,transparent_70%)]" />
                <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[60%] h-full bg-[radial-gradient(ellipse_40%_70%_at_50%_0%,rgba(251,191,36,0.10),transparent_60%)]" />
            </motion.div>

            {/* Subtle scan lines (much lighter than grid) */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.18]"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0 1px, transparent 1px 220px)",
                }}
            />

            {/* Horizon line */}
            <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 top-[48%] w-[80%] max-w-5xl h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent blur-[1px] pointer-events-none"
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8">
                {/* ─── Heading block ─── */}
                <div className="text-center max-w-5xl mx-auto">
                    {/* Live badge — creates intrigue */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2.5 glass rounded-full pl-1.5 pr-3.5 py-1.5 mb-9 font-code"
                        data-testid="hero-badge"
                    >
                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2.5 py-0.5">
                            <span className="relative flex w-1.5 h-1.5">
                                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                                <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            </span>
                            LIVE
                        </span>
                        <span className="text-[11px] sm:text-xs text-zinc-300">
                        ApexBrain v2.6 — Discover What's Holding Growth Back{" "}
                            <span className="text-cyan-300">  and Fix It</span>
                        </span>
                        <ArrowUpRight className="w-3 h-3 text-zinc-500" />
                    </motion.div>

                    {/* Title — rotating tail word creates curiosity */}
                    <h1
                        className="font-display text-[2.75rem] sm:text-[4rem] lg:text-[5.5rem] xl:text-[6.25rem] font-semibold leading-[0.95] text-white tracking-tight"
                        data-testid="hero-title"
                    > If Everything Depends on You, It's Time for
                        <motion.span
                            initial={{ opacity: 0, y: 28, filter: "blur(10px)" }}
                            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            transition={{ delay: 0.05, duration: 0.7 }}
                            className="block gradient-text"
                        >
                            a Better System.    
                        </motion.span>
                    </h1>

                    {/* Curiosity hook subtitle */}
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 0.6 }}
                        className="mt-7 font-display text-base sm:text-lg lg:text-xl text-cyan-300 font-medium"
                        data-testid="hero-tagline"
                    >
                        Because the companies that scale don't work harder -
                        they work as a system.
                    </motion.p>

                    {/* Subtitle */}
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.6 }}
                        className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-zinc-400 leading-relaxed font-light"
                        data-testid="hero-subtitle"
                    >
                        ApexBrain unifies Sales, Operations, HR, Finance and
                        Communication into one intelligent layer - so leaders
                        see everything, decide faster, and grow without
                        chaos.
                    </motion.p>

                    {/* CTAs */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                        className="mt-9 flex flex-wrap items-center justify-center gap-3"
                    >
                        <MagneticButton
                            to="/pricing"
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3.5 rounded-full hover:brightness-110 transition"
                            data-testid="hero-primary-cta"
                        >
                            Purchase Now
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                        </MagneticButton>
                        <MagneticButton
                            onClick={() => window.open("https://demo.apexbrain.in", "_blank", "noopener")}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-6 py-3.5 rounded-full hover:bg-white/10 transition"
                            data-testid="hero-secondary-cta"
                        >
                            Sign In to Demo
                            <ArrowUpRight className="w-4 h-4 text-electric" />
                        </MagneticButton>
                    </motion.div>

                  
                </div>

                {/* ─── Infrastructure Mesh centerpiece ─── */}
                <motion.div
                    style={{ y: yMesh }}
                    className="relative mt-10 sm:mt-16 lg:mt-20 overflow-hidden"
                    data-testid="hero-dashboard"
                >
                    <OrbitRings className="hidden sm:block" />
                    <div className="relative z-10">
                        <InfrastructureMesh />
                    </div>
                </motion.div>

                {/* Scroll cue */}
                <motion.button
                    onClick={() =>
                        window.scrollBy({
                            top: window.innerHeight * 0.9,
                            behavior: "smooth",
                        })
                    }
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                    className="mt-8 sm:mt-12 mx-auto flex items-center gap-2 text-[11px] font-code uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 transition group"
                    data-testid="hero-scroll-cue"
                >
                    <span>{"// see how it works"}</span>
                    <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform animate-bounce" />
                </motion.button>
            </div>

            {/* bottom fade */}
            <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-obsidian-300 to-transparent pointer-events-none" />
        </section>
    );
};
